'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { InquiryFormData } from '../../../../lib/api/types/inquiry.types';

const countryCodes = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
];

export default function InquiryForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<InquiryFormData>({
    full_name: '',
    email: '',
    phone: '',
    inquiry_type: '',
    message: '',
    city: '',
    countryCode: '+91'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpMessage, setOtpMessage] = useState<{ type: 'success' | 'error'; text: string; details?: string } | null>(null);

  const inquiryTypes = [
    'General Inquiry',
    'Program Information',
    'Application Assistance',
    'Visa Guidance',
    'Scholarship Information',
    'Partnership Opportunities',
    'Other'
  ];

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitStatus('idle');
  };

  const sendOTP = async () => {
    setIsSendingOtp(true);
    setOtpMessage(null);

    try {
      const phoneNumber = `${formData.countryCode}${formData.phone}`;
      
      const response = await fetch('/api/otp/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received:', textResponse);
        setOtpMessage({ 
          type: 'error', 
          text: 'Server error',
          details: `The server returned an unexpected response. Status: ${response.status}. Please contact support if this persists.`
        });
        return;
      }

      const data = await response.json();

      if (data.success) {
        setOtpSent(true);
        setOtpMessage({ type: 'success', text: data.message });
      } else {
        // Display detailed error message
        let errorText = data.message || 'Failed to send OTP';
        let errorDetails = '';

        // Add specific error details based on error code
        if (data.errorCode === 'RATE_LIMITED') {
          errorDetails = 'Please wait before requesting another OTP.';
        } else if (data.errorCode === 'INVALID_PHONE_FORMAT' || data.errorCode === 'INVALID_PHONE_LENGTH') {
          errorDetails = 'Please check your phone number and try again.';
        } else if (data.errorCode === 'CONFIG_MISSING' || data.errorCode === 'CHANNEL_MISSING') {
          errorDetails = 'WhatsApp service is not configured. Please contact support.';
        } else if (data.errorCode === 'AUTH_FAILED') {
          errorDetails = 'WhatsApp service authentication failed. Please contact support.';
        } else if (data.errorCode === 'SERVICE_UNAVAILABLE') {
          errorDetails = 'WhatsApp service is temporarily down. Please try again later.';
        } else if (data.errorCode === 'NETWORK_ERROR') {
          errorDetails = 'Network connection issue. Please check your internet and try again.';
        } else if (data.errorCode === 'TIMEOUT') {
          errorDetails = 'Request timed out. Please try again.';
        } else if (data.errorCode === 'DB_ERROR') {
          errorDetails = 'Database error. Please try again or contact support.';
        } else if (data.error) {
          errorDetails = data.error;
        }

        setOtpMessage({ 
          type: 'error', 
          text: errorText,
          details: errorDetails 
        });

        // Log detailed error for debugging
        console.error('OTP Send Error:', {
          message: data.message,
          error: data.error,
          errorCode: data.errorCode,
          errorDetails: data.errorDetails,
        });
      }
    } catch (error) {
      console.error('Network error sending OTP:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setOtpMessage({ 
        type: 'error', 
        text: 'Network error',
        details: `Failed to connect to server: ${errorMessage}. Please check your internet connection and try again.`
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const verifyOTP = async () => {
    setIsVerifyingOtp(true);
    setOtpMessage(null);

    try {
      const phoneNumber = `${formData.countryCode}${formData.phone}`;
      
      const response = await fetch('/api/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_number: phoneNumber, otp }),
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received:', textResponse);
        setOtpMessage({ 
          type: 'error', 
          text: 'Server error',
          details: `The server returned an unexpected response. Status: ${response.status}. Please contact support if this persists.`
        });
        return;
      }

      const data = await response.json();

      if (data.success && data.verified) {
        setOtpVerified(true);
        setOtpMessage({ type: 'success', text: data.message });
      } else {
        // Display detailed error message
        let errorText = data.message || 'Failed to verify OTP';
        let errorDetails = '';

        // Add specific error details based on error code
        if (data.errorCode === 'INVALID_OTP') {
          errorDetails = 'The OTP you entered is incorrect. Please try again.';
        } else if (data.errorCode === 'OTP_EXPIRED') {
          errorDetails = 'The OTP has expired. Please request a new one.';
        } else if (data.errorCode === 'MAX_ATTEMPTS_EXCEEDED') {
          errorDetails = 'Too many failed attempts. Please request a new OTP.';
        } else if (data.errorCode === 'OTP_NOT_FOUND') {
          errorDetails = 'No OTP found. Please request a new OTP.';
        } else if (data.errorCode === 'MISSING_OTP' || data.errorCode === 'INVALID_OTP_FORMAT') {
          errorDetails = 'Please enter a valid 6-digit OTP.';
        } else if (data.errorCode === 'DB_ERROR') {
          errorDetails = 'Database error. Please try again or contact support.';
        }

        setOtpMessage({ 
          type: 'error', 
          text: errorText,
          details: errorDetails 
        });

        // Log detailed error for debugging
        console.error('OTP Verify Error:', {
          message: data.message,
          errorCode: data.errorCode,
          errorDetails: data.errorDetails,
        });
      }
    } catch (error) {
      console.error('Network error verifying OTP:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setOtpMessage({ 
        type: 'error', 
        text: 'Network error',
        details: `Failed to connect to server: ${errorMessage}. Please check your internet connection and try again.`
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If phone not verified, send OTP first
    if (!otpVerified) {
      await sendOTP();
      return;
    }

    // If verified, proceed with form submission
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (data.success) {
        setSubmitStatus('success');
        
        // Redirect to thank you page
        setTimeout(() => {
          router.push('/thank-you-page');
        }, 1000);
      } else {
        setSubmitStatus('error');
        
        // Display detailed error message
        let errorText = data.message || 'Failed to submit inquiry';
        let errorDetails = '';

        // Add specific error details based on error code
        if (data.errorCode === 'PHONE_NOT_VERIFIED') {
          errorDetails = 'Your phone number verification has expired. Please verify again.';
          // Reset OTP verification state
          setOtpVerified(false);
          setOtpSent(false);
          setOtp('');
        } else if (data.errorCode === 'MISSING_FIELDS') {
          errorDetails = 'Please fill in all required fields.';
        } else if (data.errorCode === 'INVALID_EMAIL') {
          errorDetails = 'Please enter a valid email address.';
        } else if (data.errorCode === 'SUBMISSION_FAILED') {
          errorDetails = 'Failed to submit your inquiry. Please try again.';
        } else if (data.errorCode === 'EXTERNAL_API_ERROR') {
          errorDetails = 'External service error. Please try again later or contact support.';
        } else if (data.errorCode === 'VERIFICATION_CHECK_FAILED') {
          errorDetails = 'Failed to verify phone status. Please try again.';
        } else if (data.error) {
          errorDetails = data.error;
        }

        setErrorMessage(`${errorText}${errorDetails ? ' - ' + errorDetails : ''}`);

        // Log detailed error for debugging
        console.error('Form Submit Error:', {
          message: data.message,
          error: data.error,
          errorCode: data.errorCode,
          errorDetails: data.errorDetails,
        });
      }
    } catch (error) {
      console.error('Network error submitting form:', error);
      setSubmitStatus('error');
      setErrorMessage('Network error - Failed to connect to server. Please check your internet connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-card rounded-lg shadow-elevated p-8 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="EnvelopeIcon" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Send Us an Inquiry</h2>
            <p className="text-muted-foreground text-sm">We'll get back to you within 24 hours</p>
          </div>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start space-x-3 animate-fade-in">
            <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5" />
            <div>
              <p className="text-success font-semibold">Inquiry Submitted Successfully!</p>
              <p className="text-success/80 text-sm">We've received your message and will respond soon.</p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start space-x-3 animate-fade-in">
            <Icon name="ExclamationCircleIcon" size={20} className="text-destructive mt-0.5" />
            <div>
              <p className="text-destructive font-semibold">Submission Failed</p>
              <p className="text-destructive/80 text-sm">{errorMessage}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="full_name" className="block text-sm font-semibold text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className="w-full px-4 py-3 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth"
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                Phone Number *
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.countryCode}
                  onChange={(e) => handleChange('countryCode', e.target.value)}
                  className="px-3 py-3 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth min-w-[100px]"
                  disabled={otpVerified}
                >
                  {countryCodes.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="flex-1 px-4 py-3 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth w-full"
                  placeholder="00000-00000"
                  required
                  disabled={otpVerified}
                />
              </div>
              
              {/* OTP Verification Section */}
              {otpSent && !otpVerified && (
                <div className="mt-3 space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="flex-1 px-4 py-2 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth"
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={verifyOTP}
                      disabled={isVerifyingOtp || otp.length !== 6}
                      className="px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-all duration-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isVerifyingOtp ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={sendOTP}
                    disabled={isSendingOtp}
                    className="text-sm text-primary hover:underline disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              {otpVerified && (
                <div className="mt-2 flex items-center space-x-2 text-success text-sm">
                  <Icon name="CheckCircleIcon" size={16} />
                  <span>Phone number verified</span>
                </div>
              )}

              {/* OTP Messages */}
              {otpMessage && (
                <div className={`mt-2 p-3 rounded-lg text-sm border ${
                  otpMessage.type === 'success' ?'bg-success/10 text-success border-success/20' :'bg-destructive/10 text-destructive border-destructive/20'
                }`}>
                  <div className="flex items-start space-x-2">
                    <Icon 
                      name={otpMessage.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} 
                      size={16} 
                      className="mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="font-semibold">{otpMessage.text}</p>
                      {otpMessage.details && (
                        <p className="mt-1 text-xs opacity-90">{otpMessage.details}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-foreground mb-2">
                City
              </label>
              <input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="w-full px-4 py-3 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth"
                placeholder="Enter your city"
              />
            </div>
          </div>

          <div>
            <label htmlFor="inquiry_type" className="block text-sm font-semibold text-foreground mb-2">
              Inquiry Type *
            </label>
            <select
              id="inquiry_type"
              value={formData.inquiry_type}
              onChange={(e) => handleChange('inquiry_type', e.target.value)}
              className="w-full px-4 py-3 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth"
              required
            >
              <option value="">Select inquiry type</option>
              {inquiryTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
              Message *
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={6}
              className="w-full px-4 py-3 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-smooth resize-none"
              placeholder="Please provide details about your inquiry..."
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isSendingOtp}
            className="w-full px-6 py-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 hover:shadow-hover transition-all duration-smooth transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
          >
            {isSendingOtp ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                <span>Sending OTP...</span>
              </>
            ) : isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : otpVerified ? (
              <>
                <Icon name="PaperAirplaneIcon" size={20} />
                <span>Submit Inquiry</span>
              </>
            ) : (
              <>
                <Icon name="ShieldCheckIcon" size={20} />
                <span>Verify Phone & Submit</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}